import {
  ChevronDown,
  ChevronUp,
  ExternalLink,
  FileText,
  Quote,
} from 'lucide-react';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';

import { InspectArtifactButton } from '@/components/artifact-inspector/InspectArtifactButton';
import { InspectArtifactModal } from '@/components/artifact-inspector/InspectArtifactModal';
import { Button } from '@/components/ui/catalyst/button';
import { cn } from '@/lib/utils';
import { useGetArtifactDigestQuery } from '@/services/api/artifactDigestService';
import { ArtifactType } from '@/services/types/artifact-digest-types';

import { ArtifactTypeBadge } from './ArtifactTypeBadge';

interface ArtifactCitationCardProps {
  artifactId: string;
  artifactType: ArtifactType;
  excerpts?: string[];
  reasoning?: string;
  customContent?: React.ReactNode;
  confidenceScore?: number;
  showExcerptsDefault?: boolean;
  initialExcerptCount?: number;
}

export const ArtifactCitationCard: React.FC<ArtifactCitationCardProps> = ({
  artifactId,
  artifactType,
  excerpts = [],
  reasoning,
  customContent,
  confidenceScore,
  showExcerptsDefault = true,
  initialExcerptCount = 1,
}) => {
  const [showAllExcerpts, setShowAllExcerpts] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [isInspectModalOpen, setIsInspectModalOpen] = useState(false);
  const { data: digest, isLoading } = useGetArtifactDigestQuery(artifactId, {
    skip: !artifactId,
  });

  const { matter_id: matterId } = useParams<{ matter_id: string }>();

  const hasExcerpts = excerpts && excerpts.length > 0;
  const hasReasoning = !!reasoning;
  const hasCustomContent = !!customContent;
  const hasMultipleExcerpts = excerpts.length > initialExcerptCount;
  const hasDetails = hasReasoning || hasCustomContent;

  // Handle loading state
  if (isLoading) {
    return (
      <div className="animate-pulse p-4 mb-3 bg-gray-50 dark:bg-gray-800/20 rounded-lg border border-gray-100 dark:border-gray-700">
        <div className="h-16 bg-muted/50 rounded-sm mb-2" />
        <div className="flex justify-between items-center mt-1.5">
          <div className="h-3 w-20 bg-muted rounded-full" />
          <div className="h-3 w-16 bg-muted rounded-full" />
        </div>
      </div>
    );
  }

  // If no digest, show placeholder
  if (!digest) {
    return (
      <div className="p-4 mb-3 text-xs text-muted-foreground bg-gray-50 dark:bg-gray-800/20 rounded-lg border border-gray-100 dark:border-gray-700">
        No information available for artifact {artifactId}
      </div>
    );
  }

  const displayConfidence =
    confidenceScore !== undefined || digest.confidence !== null;
  const confidenceValue =
    confidenceScore !== undefined ? confidenceScore : digest.confidence || 0;

  // Format confidence score as percentage
  const formattedConfidence = `${Math.round(confidenceValue * 100)}%`;

  // Get confidence color based on value
  const getConfidenceColor = (score: number) => {
    if (score >= 0.8) return 'text-emerald-500';
    if (score >= 0.6) return 'text-amber-500';
    return 'text-red-500';
  };

  // Determine which excerpts to show based on showAllExcerpts state
  const visibleExcerpts = showAllExcerpts
    ? excerpts
    : excerpts.slice(0, initialExcerptCount);

  // Get remaining excerpt count
  const remainingExcerpts = excerpts.length - initialExcerptCount;

  return (
    <div className="mb-3 group bg-gray-50 dark:bg-gray-800/20 rounded-lg border border-gray-100 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-sm transition-all">
      {/* Citation Header - always visible */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 dark:border-gray-700 bg-white/60 dark:bg-gray-800/40 rounded-t-lg">
        <div className="flex items-center gap-2 min-w-0">
          <Quote className="h-4 w-4 flex-shrink-0 text-blue-500" />
          <Button
            plain
            className="p-0 hover:text-foreground truncate text-sm font-medium"
            onClick={() => setIsInspectModalOpen(true)}
            title={digest.title}
          >
            {digest.title}
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <ArtifactTypeBadge artifactType={artifactType} size="sm" />

          {displayConfidence && (
            <span
              className={cn(
                'text-xs font-medium',
                getConfidenceColor(confidenceValue),
              )}
              title={`Confidence: ${formattedConfidence}`}
            >
              {formattedConfidence}
            </span>
          )}
        </div>
      </div>

      {/* Excerpts as the main focus */}
      {hasExcerpts && (
        <div className="px-4 py-3">
          {/* First excerpt with "show more" button */}
          {visibleExcerpts.length > 0 && (
            <div className="relative">
              <blockquote className="text-sm text-gray-700 dark:text-gray-300 border-l-4 border-blue-200 dark:border-blue-700 pl-3 pr-1 py-1 italic">
                {visibleExcerpts[0]}
              </blockquote>
            </div>
          )}

          {/* Additional excerpts */}
          {showAllExcerpts && excerpts.length > 1 && (
            <div className="space-y-3 mt-3">
              {excerpts.slice(1).map((excerpt, i) => (
                <blockquote
                  key={i}
                  className="text-sm text-gray-700 dark:text-gray-300 border-l-4 border-blue-200 dark:border-blue-700 pl-3 pr-1 py-1 italic"
                >
                  {excerpt}
                </blockquote>
              ))}
            </div>
          )}

          {/* Show more/less button for excerpts */}
          {hasMultipleExcerpts && (
            <div className="mt-2">
              <button
                type="button"
                onClick={() => setShowAllExcerpts(!showAllExcerpts)}
                className="inline-flex items-center text-xs font-medium rounded px-2 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-800/30 transition-colors"
                aria-label={
                  showAllExcerpts ? 'Show fewer excerpts' : 'Show more excerpts'
                }
              >
                {showAllExcerpts ? (
                  <>
                    <ChevronUp className="h-3 w-3 mr-1" />
                    <span>Show fewer</span>
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-3 w-3 mr-1" />
                    <span>
                      Show {remainingExcerpts} more excerpt
                      {remainingExcerpts !== 1 ? 's' : ''}
                    </span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Citation Footer */}
      <div className="flex items-center justify-between px-4 py-2 border-t border-gray-100 dark:border-gray-700 text-xs text-gray-500 rounded-b-lg">
        <div className="flex items-center gap-1.5">
          <FileText className="h-3 w-3" />
          <span className="truncate">Source Document</span>
        </div>

        <div className="flex items-center gap-3">
          {/* Details toggle */}
          {hasDetails && (
            <button
              type="button"
              onClick={() => setShowDetails(!showDetails)}
              className="flex items-center hover:text-blue-600 transition-colors"
              aria-pressed={showDetails}
            >
              <span>{showDetails ? 'Hide details' : 'Details'}</span>
              {showDetails ? (
                <ChevronUp className="h-3 w-3 ml-1" />
              ) : (
                <ChevronDown className="h-3 w-3 ml-1" />
              )}
            </button>
          )}

          {/* View source link */}
          <InspectArtifactButton
            artifactId={digest.artifact_id}
            artifactType={artifactType}
            buttonClassName="flex items-center hover:text-blue-600 transition-colors"
            icon={<ExternalLink className="h-3 w-3 ml-1" />}
            label="View source"
          />
        </div>
      </div>

      {/* Expandable details section */}
      {showDetails && (hasReasoning || hasCustomContent) && (
        <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700 bg-gray-100/50 dark:bg-gray-800/40 text-sm">
          {/* Reasoning */}
          {hasReasoning && (
            <div className="mb-3">
              <div className="font-medium text-xs text-gray-500 dark:text-gray-400 mb-1">
                Reasoning:
              </div>
              <div className="p-2 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300">
                {reasoning}
              </div>
            </div>
          )}

          {/* Custom Content */}
          {hasCustomContent && (
            <div>
              <div className="font-medium text-xs text-gray-500 dark:text-gray-400 mb-1">
                Details:
              </div>
              <div>{customContent}</div>
            </div>
          )}
        </div>
      )}

      {/* One line summary as tooltip */}
      {digest.one_line_summary && (
        <div className="sr-only">{digest.one_line_summary}</div>
      )}

      {/* Modal for artifact inspection */}
      <InspectArtifactModal
        artifactId={digest.artifact_id}
        isOpen={isInspectModalOpen}
        onOpenChange={setIsInspectModalOpen}
      />
    </div>
  );
};
